import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Plus, Search, Clock, Sparkles, MoreVertical, Trash2, Pencil, ArrowRight } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Content, Conversation } from '@shared/types';
import { HistoryConversation } from '@/types/misc';
import { RenameDialogDrawer } from '@/components/history/RenameDialogDrawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function HistoryView() {
  const [searchQuery, setSearchQuery] = useState('');
  const [editingConversation, setEditingConversation] =
    useState<Conversation | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      setEditingConversation(null);
    }
  };

  const conversationQuery = useQuery<(HistoryConversation & { thumbnailUrl?: string })[]>({
    queryKey: ['conversations'],
    enabled: !!user,
    queryFn: async () => {
      const { data: conversationsData, error: conversationsError } =
        await supabase
          .from('conversations')
          .select(
            `*, first_message:messages(content), messagesCount:messages(count)`,
          )
          .eq('user_id', user?.id ?? '')
          .order('updated_at', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(1, { referencedTable: 'first_message' });

      if (conversationsError) throw conversationsError;

      const formattedConversations = await Promise.all(
        conversationsData.map(async (conv) => {
          const rawContent = conv.first_message?.[0]?.content;
          const firstMessageContent =
            typeof rawContent === 'object' && rawContent !== null
              ? (rawContent as Content)
              : { text: '' };
          const messageCount = conv.messagesCount?.[0]?.count ?? 0;

          const formattedFirstMessage = {
            text: firstMessageContent.text ?? '',
            images: firstMessageContent.images ?? [],
          };

          // Try to get a thumbnail from storage
          let thumbnailUrl: string | undefined;
          try {
            const { data: files } = await supabase.storage
              .from('images')
              .list(`${user?.id}/${conv.id}`, { limit: 5 });

            if (files && files.length > 0) {
              // Look for thumbnail.png first (captured from 3D view)
              const thumbnailFile = files.find(f => f.name === 'thumbnail.png');
              // Then look for render files (from STL uploads)
              const renderFile = files.find(f => f.name.includes('render-') && f.name.endsWith('.png'));
              // Then any other image
              const imageFile = thumbnailFile || renderFile || files.find(f =>
                f.name.endsWith('.png') || f.name.endsWith('.jpg') || f.name.endsWith('.jpeg') || f.name.endsWith('.webp')
              );

              if (imageFile) {
                const { data: signedUrl } = await supabase.storage
                  .from('images')
                  .createSignedUrl(`${user?.id}/${conv.id}/${imageFile.name}`, 3600);

                thumbnailUrl = signedUrl?.signedUrl;
              }
            }
          } catch (_e) {
            // Ignore errors fetching thumbnails
          }

          return {
            ...conv,
            created_at: conv.created_at || new Date().toISOString(),
            updated_at:
              conv.updated_at || conv.created_at || new Date().toISOString(),
            message_count: messageCount,
            first_message: formattedFirstMessage as Content,
            thumbnailUrl,
          };
        }),
      );

      return formattedConversations;
    },
  });

  useEffect(() => {
    if (conversationQuery.isError) {
      toast({
        title: 'Error',
        description: 'Failed to load conversations',
        variant: 'destructive',
      });
    }
  }, [conversationQuery.isError, toast]);

  const deleteConversation = useMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await supabase
        .from('conversations')
        .delete()
        .eq('id', conversationId);

      if (error) throw error;

      supabase.storage
        .from('images')
        .list(`${user?.id}/${conversationId}`)
        .then(({ data: list }) => {
          if (list) {
            const filesToRemove = list.map(
              (file) => `${user?.id}/${conversationId}/${file.name}`,
            );
            supabase.storage.from('images').remove(filesToRemove);
          }
        });
    },
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({ queryKey: ['conversations'] });
      const previousConversations = queryClient.getQueryData(['conversations']);
      queryClient.setQueryData(
        ['conversations'],
        (old: HistoryConversation[]) =>
          old.filter((conv) => conv.id !== conversationId),
      );
      return { previousConversations };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast({
        title: 'Success',
        description: 'Conversation deleted successfully',
      });
    },
    onError: (error: unknown, _conversationId: string, context) => {
      console.error('Error deleting conversation:', error);
      queryClient.setQueryData(
        ['conversations'],
        context?.previousConversations,
      );
      toast({
        title: 'Error',
        description: 'Failed to delete conversation',
        variant: 'destructive',
      });
    },
  });

  const renameConversation = useMutation({
    mutationFn: async ({
      conversationId,
      newTitle,
    }: {
      conversationId: string;
      newTitle: string;
    }) => {
      const { error } = await supabase
        .from('conversations')
        .update({ title: newTitle })
        .eq('id', conversationId);

      if (error) throw error;
    },
    onMutate: async ({ conversationId, newTitle }) => {
      await queryClient.cancelQueries({ queryKey: ['conversations'] });
      const previousConversations = queryClient.getQueryData(['conversations']);
      queryClient.setQueryData(
        ['conversations'],
        (old: HistoryConversation[]) =>
          old.map((conv) =>
            conv.id === conversationId ? { ...conv, title: newTitle } : conv,
          ),
      );
      return { previousConversations };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast({
        title: 'Success',
        description: 'Conversation renamed successfully',
      });
      setEditingConversation(null);
      setOpen(false);
    },
    onError: (error: unknown, _variables, context) => {
      console.error('Error renaming conversation:', error);
      queryClient.setQueryData(
        ['conversations'],
        context?.previousConversations,
      );
      toast({
        title: 'Error',
        description: 'Failed to rename conversation',
        variant: 'destructive',
      });
    },
  });

  const handleRename = () => {
    if (!editingConversation) return;
    if (!newTitle.trim()) {
      toast({
        title: 'Title cannot be empty',
        variant: 'default',
      });
      return;
    }
    renameConversation.mutate({
      conversationId: editingConversation.id,
      newTitle: newTitle.trim(),
    });
  };

  const filteredConversations =
    conversationQuery.data?.filter((conv: HistoryConversation) => {
      const messageText = conv.first_message.text ?? '';
      const title = conv.title?.toLowerCase() ?? '';
      const searchTerm = searchQuery.toLowerCase();

      return (
        title.includes(searchTerm) ||
        messageText.toLowerCase().includes(searchTerm)
      );
    }) ?? [];

  // Take the most recent conversation as featured
  const featuredConversation = filteredConversations[0];
  const remainingConversations = filteredConversations.slice(1);

  return (
    <>
      <div className="flex h-full min-w-0 flex-1 flex-col bg-dc-bg-primary">
        {/* Hero Header */}
        <div className="relative overflow-hidden border-b border-dc-border-secondary bg-gradient-to-br from-dc-bg-secondary via-dc-bg-primary to-dc-bg-tertiary px-6 pb-8 pt-12 md:px-12">
          {/* Background decoration */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-dc-accent-primary/5 via-transparent to-dc-accent-secondary/5" />
          <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-dc-accent-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-10 -left-20 h-48 w-48 rounded-full bg-dc-accent-secondary/10 blur-3xl" />

          <div className="relative z-10 mx-auto max-w-6xl">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-dc-accent-primary to-dc-accent-secondary shadow-glow-cyan">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-dc-text-primary">
                Your Files
              </h1>
            </div>
            <p className="text-dc-text-secondary mt-2 max-w-xl">
              Browse and manage your 3D models. Click any design to continue editing.
            </p>

            {/* Search Bar */}
            <div className="relative mt-6 max-w-md">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-dc-text-tertiary" />
              <Input
                placeholder="Search your files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-12 rounded-xl border-dc-border-primary bg-dc-bg-elevated/50 pl-11 text-dc-text-primary placeholder:text-dc-text-tertiary backdrop-blur-xl focus:border-dc-accent-primary focus:ring-1 focus:ring-dc-accent-primary"
              />
            </div>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="mx-auto max-w-6xl px-6 py-8 md:px-12">
            {conversationQuery.isLoading ? (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="rounded-2xl border border-dc-border-secondary bg-dc-bg-secondary p-6">
                    <Skeleton className="h-32 w-full rounded-xl bg-dc-bg-tertiary" />
                    <Skeleton className="mt-4 h-5 w-3/4 bg-dc-bg-tertiary" />
                    <Skeleton className="mt-2 h-4 w-1/2 bg-dc-bg-tertiary" />
                  </div>
                ))}
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex h-[400px] flex-col items-center justify-center text-center">
                <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-dc-bg-tertiary">
                  <MessageSquare className="h-10 w-10 text-dc-text-tertiary" />
                </div>
                {searchQuery ? (
                  <>
                    <p className="text-xl font-semibold text-dc-text-primary">
                      No matching files
                    </p>
                    <p className="mt-2 text-dc-text-secondary">
                      Try a different search term
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-xl font-semibold text-dc-text-primary">
                      No files yet
                    </p>
                    <p className="mt-2 text-dc-text-secondary">
                      Start designing to see your work here
                    </p>
                    <Button
                      onClick={() => navigate('/')}
                      className="mt-6 bg-gradient-to-r from-dc-accent-primary to-dc-accent-secondary text-dc-bg-primary shadow-glow-cyan hover:shadow-glow-cyan-lg"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Create Your First Design
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-8">
                {/* Featured Recent Creation */}
                {featuredConversation && (
                  <div className="mb-8">
                    <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-dc-text-tertiary">
                      Continue Where You Left Off
                    </h2>
                    <div
                      onClick={() => navigate(`/editor/${featuredConversation.id}`)}
                      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-dc-border-primary bg-gradient-to-br from-dc-bg-secondary to-dc-bg-tertiary p-6 transition-all duration-300 hover:border-dc-accent-primary/50 hover:shadow-[0_0_40px_rgba(0,212,255,0.15)]"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-dc-accent-primary/5 to-dc-accent-secondary/5 opacity-0 transition-opacity group-hover:opacity-100" />

                      <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-dc-accent-primary/20 to-dc-accent-secondary/20">
                              <Sparkles className="h-6 w-6 text-dc-accent-primary" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-dc-text-primary group-hover:text-dc-accent-primary transition-colors">
                                {featuredConversation.title || featuredConversation.first_message.text?.slice(0, 50) || 'Untitled Creation'}
                              </h3>
                              <div className="flex items-center gap-2 text-sm text-dc-text-tertiary">
                                <Clock className="h-3 w-3" />
                                <span>{formatDistanceToNow(new Date(featuredConversation.updated_at), { addSuffix: true })}</span>
                                <span>•</span>
                                <span>{featuredConversation.message_count} messages</span>
                              </div>
                            </div>
                          </div>
                          {featuredConversation.first_message.text && (
                            <p className="mt-3 line-clamp-2 text-dc-text-secondary">
                              {featuredConversation.first_message.text}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-3">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/editor/${featuredConversation.id}`);
                            }}
                            className="bg-gradient-to-r from-dc-accent-primary to-dc-accent-secondary text-dc-bg-primary shadow-glow-cyan"
                          >
                            Continue Editing
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-10 w-10 text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary">
                                <MoreVertical className="h-5 w-5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="border-dc-border-primary bg-dc-bg-elevated">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingConversation(featuredConversation);
                                  setNewTitle(featuredConversation.title || '');
                                  setOpen(true);
                                }}
                                className="text-dc-text-primary hover:bg-dc-bg-hover"
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteConversation.mutate(featuredConversation.id);
                                }}
                                className="text-red-400 hover:bg-dc-bg-hover"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Grid of Other Files */}
                {
                  remainingConversations.length > 0 && (
                    <div>
                      <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-dc-text-tertiary">
                        All Files ({remainingConversations.length})
                      </h2>
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {remainingConversations.map((conversation) => (
                          <div
                            key={conversation.id}
                            onClick={() => navigate(`/editor/${conversation.id}`)}
                            className="group relative cursor-pointer rounded-xl border border-dc-border-secondary bg-dc-bg-secondary overflow-hidden transition-all duration-200 hover:border-dc-accent-primary/30 hover:bg-dc-bg-tertiary"
                          >
                            {/* Thumbnail Area */}
                            <div className="relative h-32 bg-dc-bg-tertiary">
                              {conversation.thumbnailUrl ? (
                                <img
                                  src={conversation.thumbnailUrl}
                                  alt={conversation.title || 'Model preview'}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-dc-accent-primary/20 to-dc-accent-secondary/20 flex items-center justify-center">
                                  <svg className="h-10 w-10 text-dc-text-tertiary/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                  </svg>
                                </div>
                              )}
                              {/* Dropdown menu overlay */}
                              <div className="absolute top-2 right-2">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 bg-black/30 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 hover:bg-black/50">
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="border-dc-border-primary bg-dc-bg-elevated">
                                    <DropdownMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingConversation(conversation);
                                        setNewTitle(conversation.title || '');
                                        setOpen(true);
                                      }}
                                      className="text-dc-text-primary hover:bg-dc-bg-hover"
                                    >
                                      <Pencil className="mr-2 h-4 w-4" />
                                      Rename
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteConversation.mutate(conversation.id);
                                      }}
                                      className="text-red-400 hover:bg-dc-bg-hover"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>

                            {/* Content Area */}
                            <div className="p-4">

                              <h3 className="font-medium text-dc-text-primary line-clamp-1">
                                {conversation.title || conversation.first_message.text?.slice(0, 40) || 'Untitled'}
                              </h3>

                              {conversation.first_message.text && (
                                <p className="mt-1 text-sm text-dc-text-tertiary line-clamp-2">
                                  {conversation.first_message.text}
                                </p>
                              )}

                              <div className="mt-3 flex items-center gap-2 text-xs text-dc-text-muted">
                                <Clock className="h-3 w-3" />
                                <span>{formatDistanceToNow(new Date(conversation.updated_at), { addSuffix: true })}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                }
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      <RenameDialogDrawer
        open={open}
        onOpenChange={handleOpenChange}
        newTitle={newTitle}
        onNewTitleChange={setNewTitle}
        onRename={handleRename}
      />

      <button
        type="button"
        aria-label="Create new item"
        onClick={() => navigate('/')}
        className="fixed bottom-8 right-8 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-dc-accent-primary to-dc-accent-secondary text-dc-bg-primary shadow-glow-cyan md:hidden"
      >
        <Plus className="h-8 w-8" />
      </button>
    </>
  );
}
